/**
 * Host-agnostic HTTP core for the Asthra shared client (`@shared/client`).
 *
 * This shim was reconstructed IN THIS REPO to wire `guru-web/` onto the
 * main-branch, run-centric FastAPI backend (`server/`). The original
 * `@shared/client` package was authored against a doc-centric API and was
 * never committed here — see `docs/section-drafting-flow.md` / the plan.
 *
 * Every call is keyed by a `ClientConfig = { apiBase, token }`. The backend
 * authenticates via `Authorization: Bearer <JWT>` (server/auth_helpers.py) OR
 * the `access_token` cookie; we send both (bearer header when a token is
 * present, plus `credentials: "include"`). NOTE: the `?token=` share-link
 * scheme guru-web was built for has NO backend equivalent here, so a
 * share-token-only (not-signed-in) visitor cannot authenticate — see the
 * `grants` module (// NOT WIRED - PENDING).
 */

export interface ClientConfig {
  /** API origin/prefix — same-origin `/api` by default, or a full origin. */
  apiBase: string;
  /** JWT bearer token (from login) or a share token. May be empty. */
  token: string;
}

/** An Error carrying the HTTP status + parsed `detail` payload. */
export interface ApiError extends Error {
  status?: number;
  detail?: unknown;
}

function authHeaders(cfg: ClientConfig, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra ?? {}) };
  if (cfg.token) headers.Authorization = `Bearer ${cfg.token}`;
  return headers;
}

/** Join `apiBase` + path, tolerating a trailing slash on the base. */
export function apiUrl(cfg: ClientConfig, path: string): string {
  const base = cfg.apiBase.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

async function raise(resp: Response): Promise<never> {
  const body = await resp.json().catch(() => ({ detail: resp.statusText }));
  const detailValue = (body as { detail?: unknown }).detail;
  let message: string;
  if (typeof detailValue === "string") {
    message = detailValue;
  } else if (detailValue && typeof detailValue === "object" && "message" in (detailValue as Record<string, unknown>)) {
    message = String((detailValue as { message: unknown }).message);
  } else {
    message = `HTTP ${resp.status}`;
  }
  const err = new Error(message) as ApiError;
  err.status = resp.status;
  err.detail = detailValue;
  throw err;
}

/** JSON request → parsed body of type `T`. Throws `ApiError` on non-2xx. */
export async function request<T>(
  cfg: ClientConfig,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const resp = await fetch(apiUrl(cfg, path), {
    method,
    credentials: "include",
    headers: authHeaders(cfg, body !== undefined ? { "Content-Type": "application/json" } : undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) return raise(resp);
  // 204/empty bodies → undefined cast to T.
  const text = await resp.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Multipart upload (FormData). Does not set Content-Type (browser adds boundary). */
export async function upload<T>(cfg: ClientConfig, path: string, form: FormData): Promise<T> {
  const resp = await fetch(apiUrl(cfg, path), {
    method: "POST",
    credentials: "include",
    headers: authHeaders(cfg),
    body: form,
  });
  if (!resp.ok) return raise(resp);
  return resp.json();
}

/**
 * Stream a Server-Sent-Events endpoint, invoking `onEvent` for each parsed
 * `data:` JSON payload. Uses `fetch` + manual SSE parsing (not `EventSource`)
 * so we can send the `Authorization` header — mirrors the add-in's streamChat.
 * Resolves when the stream closes. `init` may carry a POST body / method.
 */
export async function streamSse(
  cfg: ClientConfig,
  path: string,
  onEvent: (event: Record<string, unknown>) => void,
  init?: { method?: string; body?: unknown; signal?: AbortSignal },
): Promise<void> {
  const hasBody = init?.body !== undefined;
  const resp = await fetch(apiUrl(cfg, path), {
    method: init?.method ?? "GET",
    credentials: "include",
    headers: authHeaders(cfg, hasBody ? { "Content-Type": "application/json" } : undefined),
    body: hasBody ? JSON.stringify(init?.body) : undefined,
    signal: init?.signal,
  });
  if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      // An SSE record may carry multiple `data:` lines; concatenate them.
      const dataLines = part
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim());
      if (dataLines.length === 0) continue;
      const payload = dataLines.join("\n");
      try {
        onEvent(JSON.parse(payload));
      } catch {
        // Skip keepalives / malformed events.
      }
    }
  }
}
